import {
  endOfMonth,
  prepareParamsForRequest,
  startOfMonth,
} from '@events/utils'
import store from '@state/store'
import axios from 'axios'

const state = {
  cached: [],
  events: null,
  eventDetails: {},
  groups: [],
  filters: {
    // default filters
    from_date: '', // beginning of the current month
    to_date: '', // end of the current month
    with_ongoing: true,
  },
}
const getters = {
  eventById(state) {
    return (eventId) =>
      state.events && state.events.find(({ id }) => id === parseInt(eventId))
  },
}

const mutations = {
  SET_EVENTS(state, events) {
    state.events = events
  },
  ADD_EVENT(state, newValue) {
    state.events = [newValue].concat(state.events)
  },
  UPDATE_EVENT(state, { eventIndex, event }) {
    state.events = state.events.map((item, index) =>
      index !== eventIndex ? item : event
    )
    state.eventDetails = { ...state.eventDetails }
    state.eventDetails[event.id] = event
  },
  SET_EVENT_DETAILS(state, { id, eventDetails }) {
    state.eventDetails = { ...state.eventDetails }
    state.eventDetails[id] = eventDetails
    if (state.events) {
      state.events = state.events.map((item) => {
        if (item.id === id) {
          return { ...item, ...eventDetails }
        }
        return item
      })
    }
  },
  SET_EVENT_JOINED_USERS(state, { id, joinedUsers }) {
    const eventDetails = { ...state.eventDetails[id], joinedUsers }
    state.eventDetails = { ...state.eventDetails }
    state.eventDetails[id] = eventDetails
    state.events = state.events.map((item) => {
      if (item.id === id) {
        return { ...item, ...eventDetails }
      }
      return item
    })
  },
  SET_EVENT_LEADERS(state, { id, leaders }) {
    const eventDetails = { ...state.eventDetails[id], leaders }
    state.eventDetails = { ...state.eventDetails }
    state.eventDetails[id] = eventDetails
    state.events = state.events.map((item) => {
      if (item.id === id) {
        return { ...item, ...eventDetails }
      }
      return item
    })
  },
  SET_EVENT_JOINED_WAITLIST_USERS(state, { id, joinedWaitlistUsers }) {
    const eventDetails = { ...state.eventDetails[id], joinedWaitlistUsers }
    state.eventDetails = { ...state.eventDetails }
    state.eventDetails[id] = eventDetails
    state.events = state.events.map((item) => {
      if (item.id === id) {
        return { ...item, ...eventDetails }
      }
      return item
    })
  },
  SET_EVENT_DETAILS_MESSAGES(state, { id, messages }) {
    const eventDetails = { ...state.eventDetails[id], messages }
    state.eventDetails = { ...state.eventDetails }
    state.eventDetails[id] = eventDetails
    state.events = state.events.map((item) => {
      if (item.id === id) {
        return { ...item, ...eventDetails }
      }
      return item
    })
  },
  SET_GROUPS(state, newValue) {
    state.groups = newValue
  },
  SET_FILTER_MONTH(state, monthDate) {
    const fromDate = startOfMonth(monthDate)
    const toDate = endOfMonth(monthDate)
    const filters = { ...state.filters }
    filters.from_date = fromDate
    filters.to_date = toDate
    state.filters = filters
  },

  SET_FILTER_INTERVAL(state, { fromDate, toDate }) {
    const filters = { ...state.filters }
    filters.from_date = fromDate
    filters.to_date = toDate
    state.filters = filters
  },

  CANCEL_EVENT(state, { id }) {
    delete state.eventDetails[id]
  },
  DETACH_EVENT(state, { id }) {
    const eventDetails = { ...state.eventDetails[id], recurringEnabled: 0 }
    state.eventDetails = { ...state.eventDetails }
    state.eventDetails[id] = eventDetails
  },
  SET_EVENT_PINNED(state, { eventId, pinned }) {
    const eventDetails = { ...state.eventDetails[eventId], pinned }
    const events = state.events.map((event) =>
      event.id === eventId ? { ...event, pinned } : event
    )
    state.eventDetails = { ...state.eventDetails }
    state.eventDetails[eventId] = eventDetails
    state.events = [...events]
  },
}

const actions = {
  fetchCauses,
  fetchEventDetails,
  fetchEvents,
  fetchFilteredEvents,
  fetchFilteredTags,
  fetchGroupsHierarchy,
  fetchTags,
  joinEvent,
  joinEventWithExtraData,
  quitEvent,
  joinEventWaitlist,
  quitEventWaitlist,
  removeJoinedUser,
  removeLeaderOfEvent,
  setEventDetails,
  setEventMessages,
  setFilterInterval,
  setFilterMonth,
  setJoinedUsers,
  checkIn,
  registrationData,
  fetchEvent,
  addUserToAttendees,
  cancelEvent,
  detachEvent,
  copyEvent,
  pinEvent,
  unpinEvent,
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}

function setFilterMonth(
  { commit, state, rootState },
  { companyId, coHostOrganizationId, monthDate }
) {
  commit('SET_FILTER_MONTH', monthDate)
}

function setFilterInterval(
  { commit, state, rootState },
  { companyId, coHostOrganizationId, fromDate, toDate }
) {
  commit('SET_FILTER_INTERVAL', { fromDate, toDate })
  return fetchEvents(
    { commit, state, rootState },
    { companyId, coHostOrganizationId }
  )
}

function fetchEvents(
  { commit, state: { filters } },
  { companyId, coHostOrganizationId }
) {
  return fetchFilteredEvents(
    { commit, state },
    { companyId, coHostOrganizationId, filters }
  )
}

function fetchFilteredEvents(
  { commit },
  { companyId, coHostOrganizationId, filters }
) {
  // Refresh the events
  commit('SET_EVENTS', null)

  // Determine the API endpoint based on which ID is provided
  let endpoint
  if (coHostOrganizationId) {
    // Fetch events where this organization is a co-host
    endpoint = `/api/v1/organization/${coHostOrganizationId}/cohost-opportunities`
  } else {
    endpoint = `/api/v1/company/${companyId}/opportunities`
  }

  // Fetch the events from the API
  return axios
    .get(endpoint, {
      params: prepareParamsForRequest(filters),
    })
    .then((response) => {
      const events = response.data
      // save the events in Vuex store
      commit('SET_EVENTS', events)
      return events
    })
}

function fetchEvent({ commit }, { eventId }) {
  return axios.get(`/api/v1/opportunity/${eventId}`).then((response) => {
    const event = response.data
    // save the events in Vuex store
    commit('SET_EVENTS', [event])
    return event
  })
}

function joinEvent({ commit }, { eventId, userId, recurring }) {
  const params = {
    opportunity_id: eventId,
    user_id: userId,
  }
  if (recurring) {
    Object.assign(params, { recurring })
  }
  return axios
    .post(`/api/v1/opportunity/join/false`, params)
    .then(({ data: joinedUsers }) => {
      commit('SET_EVENT_JOINED_USERS', {
        id: eventId,
        joinedUsers: joinedUsers,
      })
      return joinedUsers
    })
}

function joinEventWithExtraData(
  { commit },
  { eventId, userId, guestsCount, guests, registrationFormData, waitlist }
) {
  const payload = {
    opportunity_id: eventId,
    user_id: userId,
    guests_count: guestsCount,
    guests,
  }

  if (registrationFormData)
    payload.registration_form_data = registrationFormData

  return axios
    .post(`/api/v1/opportunity/join/${waitlist}`, payload)
    .then((response) => {
      if (waitlist) {
        commit('SET_EVENT_JOINED_WAITLIST_USERS', {
          id: eventId,
          joinedWaitlistUsers: response.data,
        })
      } else {
        commit('SET_EVENT_JOINED_USERS', {
          id: eventId,
          joinedUsers: response.data,
        })
      }
      return { users: response.data, waitlist: waitlist }
    })
}

function quitEvent({ commit }, { eventId, userId, recurring }) {
  const params = {
    opportunity_id: eventId,
    user_id: userId,
  }
  if (recurring) {
    Object.assign(params, { recurring })
  }
  return axios
    .post(`/api/v1/opportunity/quit/false`, params)
    .then(({ data: { joinedUsers } }) => {
      commit('SET_EVENT_JOINED_USERS', { id: eventId, joinedUsers })
      return joinedUsers
    })
}

function joinEventWaitlist({ commit }, { eventId, userId }) {
  return axios
    .post(`/api/v1/opportunity/join/true`, {
      opportunity_id: eventId,
      user_id: userId,
    })
    .then(({ data: joinedWaitlistUsers }) => {
      commit('SET_EVENT_JOINED_WAITLIST_USERS', {
        id: eventId,
        joinedWaitlistUsers,
      })
      return joinedWaitlistUsers
    })
}

function quitEventWaitlist({ commit }, { eventId, userId }) {
  return axios
    .post(`/api/v1/opportunity/quit/true`, {
      opportunity_id: eventId,
      user_id: userId,
    })
    .then(({ data: { joinedWaitlistUsers } }) => {
      commit('SET_EVENT_JOINED_WAITLIST_USERS', {
        id: eventId,
        joinedWaitlistUsers,
      })
      return joinedWaitlistUsers
    })
}

function fetchEventDetails({ commit, state, rootState }, { eventId }) {
  return axios
    .get(`/api/v1/opportunity/${eventId}/get_opportunity_details`)
    .then((response) => {
      const eventDetails = {
        id: eventId,
        eventDetails: response.data,
      }
      // save the events in Vuex store
      commit('SET_EVENT_DETAILS', eventDetails)
      return eventDetails
    })
}

function setEventMessages(
  { commit, state, rootState },
  { message, eventId, userId }
) {
  return axios
    .post('/api/v1/opportunity/new_message', {
      message: message,
      opportunity_id: eventId,
      user_id: userId,
    })
    .then(({ data }) => {
      commit('SET_EVENT_DETAILS_MESSAGES', { id: eventId, messages: data })
      return data
    })
}

function setJoinedUsers({ commit, state, rootState }, { eventId, attendees }) {
  return axios
    .post(`/api/v1/opportunity/${eventId}/addNewUsers`, {
      invites: attendees,
    })
    .then(({ data: { joinedUsers } }) => {
      commit('SET_EVENT_JOINED_USERS', { id: eventId, joinedUsers })

      return joinedUsers
    })
}

function removeJoinedUser(
  { commit, state: { eventDetails }, rootState },
  { eventId, userId }
) {
  return axios
    .get(`/api/v1/opportunity/${eventId}/removeUser/${userId}`)
    .then(({ data: hasBeenRemoved }) => {
      if (!hasBeenRemoved) return

      const allJoinedUsers = [
        ...eventDetails[eventId].joinedUsers,
        ...eventDetails[eventId].joinedWaitlistUsers,
      ]

      const joinedUsers = eventDetails[eventId].joinedUsers.filter(
        ({ id }) => id !== userId
      )
      joinedUsers.forEach((user, index) => {
        const guestsNr = user.guests.length
        joinedUsers[index].guests = user.guests.filter(
          ({ id }) => id !== userId
        )
        const removedGuestsNr = guestsNr - joinedUsers[index].guests.lenth
        if (removedGuestsNr > 0)
          joinedUsers[index].pivot.guests_count =
            joinedUsers[index].pivot.guests_count - removedGuestsNr
      })
      const joinedWaitlistUsers = eventDetails[eventId].joinedWaitlistUsers
        ? eventDetails[eventId].joinedWaitlistUsers.filter(
            ({ id }) => id !== userId
          )
        : []

      const removedUser = allJoinedUsers.find(({ id }) => id === userId)

      commit('SET_EVENT_JOINED_USERS', {
        id: eventId,
        joinedUsers,
      })

      commit('SET_EVENT_JOINED_WAITLIST_USERS', {
        id: eventId,
        joinedWaitlistUsers,
      })

      if (removedUser.guests.length) {
        store.dispatch('events/fetchEventDetails', { eventId })
      }

      return { removedUser, joinedUsers, joinedWaitlistUsers }
    })
}

function removeLeaderOfEvent(
  { commit, state: { eventDetails }, rootState },
  { eventId, userId }
) {
  return axios
    .get(`/api/v1/opportunity/${eventId}/removeLeader/${userId}`)
    .then(({ data: hasBeenRemoved }) => {
      if (!hasBeenRemoved) return

      const leaders = eventDetails[eventId].leaders.filter(
        ({ id }) => id !== userId
      )

      commit('SET_EVENT_LEADERS', { id: eventId, leaders })

      return true
    })
}

function addUserToAttendees(
  { commit, state: { eventDetails }, rootState },
  { eventId, userId }
) {
  return axios
    .get(`/api/v1/opportunity/${eventId}/addUserToAttendees/${userId}`)
    .then(({ data: hasBeenAddedToAttendees }) => {
      if (!hasBeenAddedToAttendees) return

      const user = eventDetails[eventId].joinedWaitlistUsers.find(
        ({ id }) => id === userId
      )

      eventDetails[eventId].joinedUsers.push(user)

      const joinedWaitlistUsers = eventDetails[eventId].joinedWaitlistUsers
        ? eventDetails[eventId].joinedWaitlistUsers.filter(
            ({ id }) => id !== userId
          )
        : []

      commit('SET_EVENT_JOINED_WAITLIST_USERS', {
        id: eventId,
        joinedWaitlistUsers,
      })

      return { user, joinedWaitlistUsers }
    })
}

function fetchGroupsHierarchy({ commit, dispatch }, { companyId }) {
  return axios
    .get(`/api/v1/company/${companyId}/groupsHierarchy`)
    .then(({ data }) => {
      commit('SET_GROUPS', data)
      return data
    })
}

function setEventDetails({ commit }, eventDetails) {
  commit('SET_EVENT_DETAILS', eventDetails)
  return eventDetails
}

function fetchCauses({ commit, dispatch }, { causeIds }) {
  const params = (causeIds.length && `/${causeIds.join(',')}`) || ''
  return axios
    .get(`/api/v1/events-filters${params}`)
    .then(({ data }) => ({ data: data.causes || [] }))
}

function fetchTags({ commit, dispatch }, { tagIds }) {
  return axios
    .get(`/api/v1/tags/${tagIds.join(',')}`)
    .then((repsonse) => repsonse)
}

function fetchFilteredTags({ commit, dispatch }, { startingWith }) {
  return axios
    .get(`/api/v1/tags/startingWith/${startingWith}`)
    .then((repsonse) => repsonse)
}

function checkIn({ commit }, { eventId, userId, checkedInValue, isGuest }) {
  return axios
    .post(`/api/v1/opportunity/check_in_user`, {
      opportunity_id: eventId,
      user_id: userId,
      value: checkedInValue,
      is_guest: isGuest,
    })
    .then(({ data: response }) => {
      commit('SET_EVENT_JOINED_USERS', {
        id: eventId,
        joinedUsers: response.joinedUsers,
      })
      return response
    })
}

function registrationData({ commit }, { eventId }) {
  return axios
    .get(`/api/v1/opportunity/${eventId}/registration_data`)
    .then(({ data }) => {
      return data
    })
}

function cancelEvent({ commit }, { eventId }) {
  return axios.get(`/api/v1/opportunity/${eventId}/cancel`).then((response) => {
    // save the events in Vuex store
    commit('CANCEL_EVENT', { id: eventId })
    return response
  })
}

function detachEvent({ commit }, { eventId }) {
  return axios.get(`/api/v1/opportunity/${eventId}/detach`).then((response) => {
    if (response) {
      // save the events in Vuex store
      commit('DETACH_EVENT', { id: eventId })
    }
    return response
  })
}

function copyEvent({ commit }, { eventId }) {
  return axios.get(`/api/v1/opportunity/${eventId}/copy`).then(({ data }) => {
    commit('ADD_EVENT', data)
    commit('SET_EVENT_DETAILS', {
      id: data.id,
      eventDetails: data,
    })
    return data
  })
}

function pinEvent({ commit }, { eventId }) {
  return axios.post(`/api/v1/opportunity/${eventId}/pin`).then((response) => {
    if (!response) return
    commit('SET_EVENT_PINNED', { eventId, pinned: true })
    return response
  })
}

function unpinEvent({ commit }, { eventId }) {
  return axios.post(`/api/v1/opportunity/${eventId}/unpin`).then((response) => {
    if (!response) return
    commit('SET_EVENT_PINNED', { eventId, pinned: false })
    return response
  })
}
